"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "./AuthProvider";
import HoverLabel from "./HoverLabel";

/** FriendsPanel broadcasts its current incoming-request count on load and after
 *  every accept/decline/block, so the dot clears without a reload. Mirrors the
 *  bell's INBOX_READ_EVENT pattern. `detail` is the new count. */
export const FRIENDS_CHANGED_EVENT = "podlog:friends-changed";

interface FriendsButtonProps {
  /** "rail" matches the desktop header pill; "floating" is a standalone. */
  variant?: "rail" | "floating";
}

/** Header friends icon: deep-links to the friends panel (`?panel=friends`) and
 *  shows a red dot when incoming requests are waiting. The count is seeded from
 *  `/api/auth/me` (via the auth context) and corrected live by FriendsPanel —
 *  deliberately no focus-refresh or cross-tab sync, unlike the bell, since a
 *  dot doesn't justify a /me round trip on every tab focus. A request arriving
 *  mid-session surfaces on the next navigation.
 *  Renders nothing for signed-out users. */
export default function FriendsButton({ variant = "rail" }: FriendsButtonProps) {
  const { user, initialPendingFriends } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [pending, setPending] = useState(0);

  // Seed / re-seed from the auth context (the /me response) — no extra request.
  useEffect(() => {
    setPending(user ? initialPendingFriends : 0);
  }, [user, initialPendingFriends]);

  useEffect(() => {
    if (!user) {
      setPending(0);
      return;
    }
    const onChanged = (e: Event) => {
      const next = (e as CustomEvent<number>).detail;
      if (typeof next === "number") setPending(next);
    };
    window.addEventListener(FRIENDS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(FRIENDS_CHANGED_EVENT, onChanged);
  }, [user]);

  if (!user) return null;
  if (pathname?.startsWith("/admin")) return null;
  // On the home page AppShell renders its own rail-variant instance, so the
  // global floating one from layout.tsx must not double up (as UserMenu does).
  if (variant === "floating" && pathname === "/") return null;

  const hasPending = pending > 0;

  return (
    <div className="group relative flex-shrink-0">
      <button
        onClick={() => router.push("/?panel=friends")}
        aria-label={
          hasPending
            ? `Friends (${pending} pending ${pending === 1 ? "request" : "requests"})`
            : "Friends"
        }
        className={`relative flex flex-shrink-0 items-center justify-center rounded-full border border-sky-900/60 text-sky-200 transition-colors hover:bg-white/[0.1] ${
          variant === "rail" ? "h-9 w-9 bg-white/[0.06]" : "h-11 w-11 bg-ocean-900/90 shadow-lg backdrop-blur"
        }`}
      >
        {/* Two overlapping people — the classic "friends/contacts" glyph. The
            Groups rail tile uses a denser three-person cluster so the two icons
            don't read as the same thing. */}
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 text-sky-200/80"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 19.5a6 6 0 0 1 12 0" />
          <path d="M16.5 5.6a3.2 3.2 0 0 1 0 6" />
          <path d="M18 14.2a6 6 0 0 1 3 5.3" />
        </svg>
        {hasPending && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-ocean-900" />
        )}
      </button>
      <HoverLabel>Friends</HoverLabel>
    </div>
  );
}
